/**
 * SortPill — v3 visual for the legacy SortSelector.
 *
 * Logic is ported 1:1 from
 * `_legacy/components/Shared/Table/widgets/TopBar/SortSelector.jsx`:
 *   - Trigger renders only when `sorting` has an active entry
 *     (mirrors the `if (!sorting?.length) return null` guard).
 *   - Local state for the chosen column + direction with
 *     commit-on-apply: closing the panel without pressing "Apply"
 *     resets to the current `sorting` values.
 *   - Options are built from the passed-in `columns` array, sorted
 *     alphabetically by header, filtered to sortable columns with a
 *     string header so the radio list stays consistent. The optional
 *     `excludeSortKeys` array hides specific accessor keys (e.g.
 *     patient identity columns on the patient dashboard).
 *   - `setSorting([{ id, desc }])` shape matches what MRT expects.
 *
 * Only the markup, spacing and tokens are new. Every behavioural
 * decision (reset on cancel, apply-to-commit, direction toggle,
 * alphabetical ordering) follows the legacy component to keep muscle
 * memory intact for users of existing tables.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { ChevronDownIcon } from './icons';
import {
  PillRoot as Root,
  PillTrigger as Trigger,
  PillLabel as TriggerLabel,
  PillValue as TriggerValue,
  PillPanel as Panel,
  PillPanelHeader as PanelHeader,
  PillPanelTitle as PanelTitle,
} from './pillStyles';

const DirToggle = styled('div')({
  display: 'inline-flex',
  padding: 2,
  gap: 2,
  background: 'var(--qhs-surface-hover)',
  borderRadius: 6,
});

const DirButton = styled('button', {
  shouldForwardProp: prop => prop !== '$active',
})(({ $active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 8px',
  background: $active ? 'var(--qhs-surface)' : 'transparent',
  color: $active ? 'var(--qhs-text-1)' : 'var(--qhs-text-2)',
  border: 'none',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'background-color 120ms ease, color 120ms ease',
  boxShadow: $active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
  '& svg': { width: 12, height: 12 },
  '&:hover': { color: 'var(--qhs-text-1)' },
}));

const OptionList = styled('div')({
  maxHeight: 260,
  overflowY: 'auto',
  borderTop: '1px solid var(--qhs-border)',
  borderBottom: '1px solid var(--qhs-border)',
  padding: '4px 0',
  marginBottom: 10,
});

const Option = styled('label')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 10px',
  cursor: 'pointer',
  borderRadius: 6,
  fontSize: 13,
  color: 'var(--qhs-text-1)',
  transition: 'background-color 120ms ease',
  '&:hover': { background: 'var(--qhs-surface-hover)' },
  '& input': {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
  },
});

const RadioDot = styled('span', {
  shouldForwardProp: prop => prop !== '$checked',
})(({ $checked }) => ({
  width: 14,
  height: 14,
  borderRadius: '50%',
  border: `1.5px solid ${$checked ? 'var(--qhs-brand)' : 'var(--qhs-border-strong)'}`,
  background: 'var(--qhs-surface)',
  position: 'relative',
  flexShrink: 0,
  transition: 'border-color 120ms ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 2,
    borderRadius: '50%',
    background: 'var(--qhs-brand)',
    transform: $checked ? 'scale(1)' : 'scale(0)',
    transition: 'transform 140ms ease',
  },
}));

const OptionLabel = styled('span')({
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Actions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
});

const ApplyBtn = styled('button')({
  padding: '8px 14px',
  background: 'var(--qhs-brand)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'filter 120ms ease, box-shadow 120ms ease',
  boxShadow: '0 1px 2px rgba(46,72,211,0.2)',
  // Hover darkens the brand surface slightly instead of swapping to
  // `--qhs-primary` (which is *lighter* than brand and made the
  // button read as wrong-coloured on hover). Same trick we'll reuse
  // for any other brand-filled button.
  '&:hover': {
    filter: 'brightness(0.92)',
    boxShadow: '0 2px 6px rgba(46,72,211,0.28)',
  },
  '&:focus-visible': {
    outline: '2px solid var(--qhs-brand)',
    outlineOffset: 2,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    filter: 'none',
  },
});

const SortAscIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 6l7 0" />
    <path d="M4 12l7 0" />
    <path d="M4 18l9 0" />
    <path d="M15 9l3 -3l3 3" />
    <path d="M18 6l0 12" />
  </svg>
);

const SortDescIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 6l9 0" />
    <path d="M4 12l7 0" />
    <path d="M4 18l7 0" />
    <path d="M15 15l3 3l3 -3" />
    <path d="M18 6l0 12" />
  </svg>
);

// Stable default so the `sortableColumns` memo keeps its identity when a
// caller omits `excludeSortKeys` — a fresh `[]` each render recomputed the
// memo, which (via the seed effect) reverted the user's in-panel selection
// on every render.
const EMPTY_EXCLUDE_KEYS = [];

const SortPill = ({ sorting, setSorting, columns, excludeSortKeys = EMPTY_EXCLUDE_KEYS }) => {
  const { btn = {}, tables = {} } = useSelector(state => state.i18n) || {};
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // --- Commit-on-apply local state, mirrors the legacy component.
  const currentKey = sorting?.[0]?.id;
  const currentDesc = sorting?.[0]?.desc;
  const [_sortKey, _setSortKey] = useState(() => currentKey);
  const [_desc, _setDesc] = useState(() => currentDesc ?? false);

  const sortableColumns = useMemo(
    () =>
      (columns || [])
        .filter(c => c.accessorKey && c.enableSorting !== false && typeof c.header === 'string')
        .filter(c => !excludeSortKeys.includes(c.accessorKey))
        .sort((a, b) => a.header.localeCompare(b.header)),
    [columns, excludeSortKeys]
  );

  // Seed the pending selection from the committed sorting when the panel
  // opens, and re-seed if the committed sorting changes externally while
  // open. Deliberately NOT keyed on `sortableColumns`: that memo takes a
  // fresh identity on most renders, so including it re-ran this effect on
  // every render and instantly reverted the radio / direction the user
  // just clicked (selection flashes, then snaps back to the previous).
  useEffect(() => {
    if (!open) return;
    _setSortKey(currentKey ?? sortableColumns[0]?.accessorKey);
    _setDesc(currentDesc ?? false);
  }, [open, currentKey, currentDesc]);

  // Outside-click = cancel. Reset pending state before closing so the
  // next open shows the committed values.
  useEffect(() => {
    if (!open) return undefined;
    const handle = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        _setSortKey(currentKey ?? sortableColumns[0]?.accessorKey);
        _setDesc(currentDesc ?? false);
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, currentKey, currentDesc]);

  const currentColumnHeader = useMemo(() => {
    const fromSortable = sortableColumns.find(c => c.accessorKey === currentKey);
    if (fromSortable) return fromSortable.header;
    const fromAll = (columns || []).find(c => c.accessorKey === currentKey);
    return fromAll?.header || currentKey;
  }, [sortableColumns, columns, currentKey]);

  if (!sortableColumns.length) return null;

  const applySorting = () => {
    // `setSorting` here is `useFetchList`'s `onSort`, which expects
    // a React-style updater function (legacy SortSelector called it
    // the same way: `setSorting(pr => [{ id, desc }])`). Passing a
    // raw array made `onSort` try to invoke it as `newSort(sort)`
    // and threw "newSort is not a function".
    setSorting?.(() => [{ id: _sortKey, desc: _desc }]);
    setOpen(false);
  };

  return (
    <Root ref={rootRef}>
      <Trigger
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {currentKey ? (
          <>
            <TriggerLabel>{tables.sortedBy || 'Sorted by: '}</TriggerLabel>
            <TriggerValue>{currentColumnHeader}</TriggerValue>
          </>
        ) : (
          <TriggerLabel>{tables.sort || 'Sort'}</TriggerLabel>
        )}
        <span style={{ color: 'var(--qhs-text-3)', display: 'inline-flex' }}>
          <ChevronDownIcon />
        </span>
      </Trigger>

      {open && (
        <Panel role="dialog" aria-label={tables.sortTableBy || 'Sort table by'}>
          <PanelHeader>
            <PanelTitle>{tables.sortTableBy || 'Sort table by'}</PanelTitle>
            <DirToggle role="group" aria-label="Sort direction">
              <DirButton type="button" $active={_desc === true} onClick={() => _setDesc(true)}>
                <SortDescIcon />
                {tables.desc || 'Desc'}
              </DirButton>
              <DirButton type="button" $active={_desc === false} onClick={() => _setDesc(false)}>
                <SortAscIcon />
                {tables.asc || 'Asc'}
              </DirButton>
            </DirToggle>
          </PanelHeader>

          <OptionList role="radiogroup" aria-label="Sort column">
            {sortableColumns.map(col => {
              const checked = _sortKey === col.accessorKey;
              return (
                <Option key={col.accessorKey}>
                  <input
                    type="radio"
                    name="sort-column"
                    value={col.accessorKey}
                    checked={checked}
                    onChange={() => _setSortKey(col.accessorKey)}
                  />
                  <RadioDot $checked={checked} />
                  <OptionLabel>{col.header}</OptionLabel>
                </Option>
              );
            })}
          </OptionList>

          <Actions>
            <ApplyBtn type="button" onClick={applySorting}>
              {btn.applySorting || 'Apply sorting'}
            </ApplyBtn>
          </Actions>
        </Panel>
      )}
    </Root>
  );
};

export default SortPill;
